import React from 'react';
import { OpsInstruction, WEATHER_EVENT_CLAZZ_VALUE, WeatherEvent } from 'app/model/SOL49';
import { Button, Col, Form, Input, Popconfirm, Row, Select } from 'antd';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import Utils from 'app/common/util/Utils';
import ConditionalRender from 'app/common/components/ConditionalRender';
import TimeUtils from 'app/common/util/TimeUtils';
import { RefLocation, TvRefLocation } from 'app/model/CORE';
import {
    MonitoredEventForm,
    MonitoredEventFormProps,
    MonitoredEventFormState,
    MonitoredEventFormValues,
} from 'app/components/panels/predict/MonitoredEventForm';
import { FilePdfOutlined } from '@ant-design/icons';
import { Flex } from 'app/components/common/Flex';
import { SidePanelSeparator } from 'app/components/common/SidePanelSeparator';
import { PDFViewer } from 'app/components/common/PDFViewer';
import { MonitoredEventPanelWrapper } from 'app/components/panels/predict/MonitoredEventPanelWrapper';
import { MonitoredEventsFilters } from 'app/components/dashboard/global/events/MonitoredEventFilters';
import { DefaultOptionType } from 'rc-select/lib/Select';
import { MonitoredEventType } from '../../../../common/util/MonitoredEventUtils';

type WeatherEventFormValues = MonitoredEventFormValues<WeatherEvent> &
    Omit<WeatherEvent, 'opsInstructions'> & {
        refLocs: string[];
        opsInstructions: string[];
    };

interface WeatherEventFormProps extends MonitoredEventFormProps<WeatherEvent> {
    refLocs: TvRefLocation[];
    defaultRefLoc: TvRefLocation;
    opsInstructions: OpsInstruction[];
    filters: MonitoredEventsFilters;
}

interface WeatherEventFormState extends MonitoredEventFormState {
    refLocsSelectOpen: boolean;
    opsInstrSelectOpen: boolean;
    typeSelectOpen: boolean;
    showPDF: boolean;
    filters: DefaultOptionType[];
}

export class WeatherEventForm extends MonitoredEventForm<
    WeatherEvent,
    WeatherEventFormValues,
    WeatherEventFormProps,
    WeatherEventFormState
> {
    private static PDF_PANEL_WIDTH = 60;
    private static showPDF = false;

    constructor(props: WeatherEventFormProps) {
        super(props);

        const types = this.props.filters?.types?.map(type => ({ value: type, label: type })) || [];
        this.state = {
            ...this.state,
            refLocsSelectOpen: false,
            opsInstrSelectOpen: false,
            typeSelectOpen: false,
            showPDF: false,
            filters: types,
        };
    }

    componentDidUpdate(prevProps: Readonly<MonitoredEventFormProps<WeatherEvent>>) {
        super.componentDidUpdate(prevProps);
        if (this.props.event?.id !== prevProps?.event?.id && this.state.showPDF) {
            this.togglePdf();
        }
    }

    private togglePdf = () => {
        this.setState({ showPDF: !this.state.showPDF }, () => {
            WeatherEventForm.showPDF = this.state.showPDF;
            this.props.onWidthChange(WeatherEventForm.getWidth());
        });
    };

    render() {
        const event = this.props.event;
        return (
            <Flex>
                <Form
                    ref={this.formRef}
                    style={{ width: MonitoredEventForm.FORM_WIDTH_EM }}
                    layout="vertical"
                    initialValues={this.defaultValues()}
                    onValuesChange={() =>
                        this.setState({
                            updateDisabled: this.isUpdateDisabled(),
                            createDisabled: this.isCreateDisabled(),
                        })
                    }>
                    <Form.Item name="clazz" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                    <Form.Item name="id" hidden>
                        <Input type="hidden" />
                    </Form.Item>
                    {MonitoredEventForm.FORM_TITLE_ITEM}
                    <Form.Item label="Type" name="type">
                        <Select
                            size={'small'}
                            options={[
                                { label: MonitoredEventType.WEATHER_EVENT, value: MonitoredEventType.WEATHER_EVENT },
                            ]}
                            open={this.state.typeSelectOpen}
                            onDropdownVisibleChange={open => this.setState({ typeSelectOpen: open })}
                            onChange={() => this.setState({ typeSelectOpen: false })}
                        />
                    </Form.Item>
                    {MonitoredEventForm.FORM_VALIDITY_ITEM}
                    <ConditionalRender condition={Utils.isNotExists(event.base64PDF)}>
                        {MonitoredEventForm.FORM_DESCRIPTION_ITEM(12)}
                    </ConditionalRender>
                    <ConditionalRender condition={Utils.isExists(event.base64PDF)}>
                        <Form.Item
                            label={
                                <span>
                                    Description <FilePdfOutlined title="Show pdf" onClick={this.togglePdf} />
                                </span>
                            }
                            name="description">
                            <Input.TextArea rows={12} className="description" />
                        </Form.Item>
                    </ConditionalRender>
                    {this.FORM_SELECT_ITEM(
                        'Reference locations',
                        'refLocs',
                        this.getOptionsFromRefLocs(this.props.refLocs),
                        this.state.refLocsSelectOpen,
                        open => this.setState({ refLocsSelectOpen: open }),
                    )}
                    {this.FORM_SELECT_ITEM(
                        'OPS instructions',
                        'opsInstructions',
                        this.getOptionsFromOpsInstr(this.props.opsInstructions),
                        this.state.opsInstrSelectOpen,
                        open => this.setState({ opsInstrSelectOpen: open }),
                    )}
                    <Row gutter={8} style={{ marginTop: 'auto' }}>
                        <Col span={this.isNewEvent() ? 20 : 16}>
                            <ConditionalRender condition={Utils.isExists(this.props.event?.lastUpdate)}>
                                <span> Last update: {TimeUtils.formatDateHHMM(event?.lastUpdate)}</span>
                            </ConditionalRender>
                        </Col>
                        <ConditionalRender condition={!this.isNewEvent()}>
                            <Col span={4}>
                                <Popconfirm
                                    title="Delete the Weather/Event note"
                                    okText="Yes"
                                    cancelText="No"
                                    onConfirm={this.props.onDelete}>
                                    <Button>Delete</Button>
                                </Popconfirm>
                            </Col>
                        </ConditionalRender>
                        <Col span={4}>
                            <ConditionalRender condition={this.isNewEvent()}>
                                <Button onClick={this.onSubmit} disabled={this.state.createDisabled}>
                                    Create
                                </Button>
                            </ConditionalRender>
                            <ConditionalRender condition={!this.isNewEvent()}>
                                <Button onClick={this.onSubmit} disabled={this.state.updateDisabled}>
                                    Update
                                </Button>
                            </ConditionalRender>
                        </Col>
                    </Row>
                </Form>
                <ConditionalRender condition={this.state.showPDF}>
                    <SidePanelSeparator hideSeparator={false} onHideSidePanel={this.togglePdf} />
                    <Flex>
                        <PDFViewer file={event?.base64PDF} />
                    </Flex>
                </ConditionalRender>
            </Flex>
        );
    }

    private defaultValues = (): WeatherEventFormValues => {
        return {
            clazz: WEATHER_EVENT_CLAZZ_VALUE,
            description: '',
            id: '',
            lastUpdate: undefined,
            opsInstructions: [],
            refLocs: [],
            title: '',
            validity: [],
        };
    };

    protected toFormValues(event: WeatherEvent): WeatherEventFormValues {
        return {
            ...event,
            clazz: WEATHER_EVENT_CLAZZ_VALUE,
            validity: TimePeriodUtils.timePeriodToMoment(event?.validity),
            refLocs: event?.refLocs?.map(refLoc => refLoc.refId) || [],
            opsInstructions: event?.opsInstructions?.map(opsInstr => opsInstr.id) || [],
        };
    }

    protected fromFormValues(values: WeatherEventFormValues) {
        return {
            ...values,
            validity: TimePeriodUtils.momentToTimePeriod(values.validity),
            refLocs: this.getRefLocsFromOptions(values.refLocs),
            opsInstructions: this.getOpsInstrFromOptions(values.opsInstructions),
            base64PDF: this.props.event?.base64PDF,
        };
    }

    protected isUpdateDisabled = () => {
        if (!this.formRef.current?.getFieldsValue() || !this.props.event) return true;
        const values: WeatherEventFormValues = this.formRef.current.getFieldsValue();
        const event = this.props.event;

        return (
            super.isUpdateDisabled() &&
            values.description === event.description &&
            values.type === event.type &&
            Utils.isShallowEquals(
                values.refLocs,
                event.refLocs?.map(ref => ref.refId),
            ) &&
            Utils.isShallowEquals(
                values.opsInstructions,
                event.opsInstructions?.map(ops => ops.id),
            )
        );
    };

    protected isCreateDisabled() {
        if (!this.formRef.current?.getFieldsValue()) return true;
        const values: WeatherEventFormValues = this.formRef.current.getFieldsValue();

        return super.isCreateDisabled() || Utils.isEmptyString(values.type);
    }

    private getRefLocsFromOptions = (values: string[]) => {
        // Try to map to a "Valid" refLoc from the client known reflocs, otherwise keep the one of the event
        return values.map(
            v =>
                this.props.refLocs.find(refLoc => refLoc?.refId === v) ||
                this.props.event?.refLocs?.find(ref => ref.refId === v),
        );
    };

    private getOptionsFromRefLocs = (refLocs: RefLocation[]) => {
        if (!refLocs) return [];

        return refLocs.map(refLoc => ({
            value: refLoc.refId,
            label: `${refLoc.refId}/${refLoc.refType}`,
        }));
    };

    private getOpsInstrFromOptions = (values: string[]) => {
        return values.map(id => this.props.opsInstructions.find(ops => ops.id === id));
    };

    private getOptionsFromOpsInstr = (opsInstr: OpsInstruction[]) => {
        if (!opsInstr) return [];

        return opsInstr.map(opsInstr => ({
            value: opsInstr.id,
            label: opsInstr.title,
        }));
    };

    static getWidth = () => {
        const baseWidth = Number(MonitoredEventPanelWrapper.WIDTH.slice(0, -2));
        return `${baseWidth + (WeatherEventForm.showPDF ? WeatherEventForm.PDF_PANEL_WIDTH : 0)}em`;
    };
}
